import {
    Box,
    Button,
    Typography,
    Grid,
    TextField,
    MenuItem,
    CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DrawerHeader } from '../../../components/Drawer/DrawerHeader';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import { showAlert } from '../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../constants/values';
import dayjs from 'dayjs';
import DateTimePickerComponent from '../../../components/DateTimePickerComponent';
import {
    useAsignarResultadoExamenEmpleadoMutation,
    useGetAptitudesQuery,
    useLazyGetPdfResultadosExamenEmpleadoQuery
} from '../../../services/periodicosApi';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { downloadBase64File } from '../../../utils/common';

interface PeriodicosDrawerProps {
    selectedIds: string[];
    setOpen: (state: string) => void;
    reloadGrid: () => void;
    selectedEmployee: any;
    isReconfirmation?: boolean;
}

const PeriodicosAsignarResultadoDrawer = ({
    selectedIds,
    setOpen,
    reloadGrid,
    selectedEmployee,
    isReconfirmation
}: PeriodicosDrawerProps) => {
    const { t } = useTranslation('periodicos');
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const [archivoResultado, setArchivoResultado] = useState<File | null>(null);
    const [fechaResultado, setFechaResultado] = useState<any>(null);
    const [idAptitud, setIdAptitud] = useState<number | null>(null);
    const [observaciones, setObservaciones] = useState<string>('');
    const [triggerDownloadPdf] = useLazyGetPdfResultadosExamenEmpleadoQuery();
    const [downloading, setDownloading] = useState(false);

    const idEstadoEmpleadoExpuesto =
        selectedEmployee?.examenEmpleado?.idEstadoEmpleadoExpuesto ||
        selectedEmployee?.estadoEmpleadoExpuesto;

    const isReadOnly = idEstadoEmpleadoExpuesto === 5;
    const isAuditor = idEstadoEmpleadoExpuesto === 6;
    const archivoResultadoPath = selectedEmployee?.examenEmpleado?.archivoResultadoPath || null;

    useEffect(() => {
        const fecha =
            selectedEmployee?.examenEmpleado?.fechaResultado ??
            selectedEmployee?.fechaRealizacion ??
            null;

        const aptitudId = selectedEmployee?.examenEmpleado?.aptitud?.id;
        const obs = selectedEmployee?.examenEmpleado?.observacion;

        if (fecha) {
            setFechaResultado(dayjs(fecha));
        }

        if (aptitudId) setIdAptitud(aptitudId);
        if (obs) setObservaciones(obs);
    }, [selectedEmployee]);

    const { data: aptitudes = [] } = useGetAptitudesQuery();
    const [asignarResultado, { isLoading }] = useAsignarResultadoExamenEmpleadoMutation();

    const handleCloseDrawer = () => setOpen('');

    const handleSave = async () => {
        const idExamenEmpleado = parseInt(selectedIds[0].split('-')[0], 10);
        if (!fechaResultado) return;

        const payload = {
            ...(idAptitud !== null && { idAptitud }),
            fechaResultado: dayjs(fechaResultado).toISOString(),
            observaciones,
            archivoResultado
        };

        try {
            await asignarResultado({ idExamenEmpleado, payload }).unwrap();
            enqueueSnackbar(t('drawer.messages.assignSuccessResults'), { variant: 'success' });
            reloadGrid();
            setOpen('');
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo asignar el resultado del examen',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleDownloadPdf = async (idExamenEmpleado: number) => {
        try {
            setDownloading(true);
            const response = await triggerDownloadPdf(idExamenEmpleado).unwrap();

            if (
                typeof response === 'object' &&
                'data' in response &&
                typeof (response as any).data === 'string'
            ) {
                const fileUrl = (response as any).data;
                await downloadBase64File(fileUrl, idExamenEmpleado, 'resultado');
            } else {
                console.error(response);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setDownloading(false);
        }
    };

    const filteredAptitudes = isReconfirmation ? aptitudes.filter((a) => a.id !== 4) : aptitudes;

    return (
        <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <Grid container flexDirection="column">
                <DrawerHeader
                    onIconClick={handleCloseDrawer}
                    text={` ${
                        selectedEmployee?.examenEmpleado?.empleado?.nombre ||
                        selectedEmployee?.nombre ||
                        ''
                    } - ${
                        selectedEmployee?.examenEmpleado?.riesgo?.codigo ||
                        selectedEmployee?.riesgo?.codigo ||
                        ''
                    } ${
                        selectedEmployee?.examenEmpleado?.riesgo?.descripcion ||
                        selectedEmployee?.riesgo?.descripcion ||
                        ''
                    }`}
                />
                <Typography sx={{ fontWeight: 'bold', mb: 2, mt: 4 }}>
                    {t('drawer.exam.subtitle')}
                </Typography>

                <Box>
                    <Typography variant="body1">
                        {selectedEmployee?.examenEmpleado?.examen?.descripcion ||
                            (selectedEmployee?.examenImportacion?.descripcion &&
                                `${selectedEmployee?.examenImportacion?.descripcion}:  ${selectedEmployee?.examenReconfirmacion}`) ||
                            '-'}
                    </Typography>
                </Box>

                <Typography sx={{ fontWeight: 'bold', mt: 4 }}>
                    {t('drawer.resultado.title')}
                </Typography>

                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12} md={6}>
                        <DateTimePickerComponent
                            label={t('drawer.resultado.date')}
                            value={fechaResultado}
                            onChange={setFechaResultado}
                            disableFuture
                            fullWidth
                            disabled={isReadOnly || isAuditor}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            label={t('drawer.resultado.aptitud')}
                            value={idAptitud ?? ''}
                            onChange={(e) => setIdAptitud(Number(e.target.value))}
                            fullWidth
                            disabled={isReadOnly}
                        >
                            {filteredAptitudes.map((aptitud) => (
                                <MenuItem key={aptitud.id} value={aptitud.id}>
                                    {aptitud.nombre}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sx={{ m: 0.5, p: 0 }}>
                        {isAuditor ? (
                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<UploadFileIcon />}
                                sx={{ p: 2, borderRadius: '5px', alignItems: 'initial' }}
                                onClick={() =>
                                    handleDownloadPdf(
                                        selectedEmployee?.examenEmpleado?.id ||
                                            selectedEmployee?.examenImportacion?.id
                                    )
                                }
                                disabled={downloading}
                            >
                                {downloading
                                    ? t('drawer.resultado.downloading')
                                    : t('drawer.resultado.viewFile')}
                            </Button>
                        ) : (
                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                startIcon={<UploadFileIcon />}
                                sx={{ p: 2, borderRadius: '5px', alignItems: 'initial' }}
                                disabled={isReadOnly}
                            >
                                {isReadOnly
                                    ? t('drawer.result.uploaded')
                                    : archivoResultado
                                    ? archivoResultado.name
                                    : t('drawer.result.upload')}
                                <input
                                    type="file"
                                    accept="application/pdf"
                                    hidden
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file && file.type === 'application/pdf') {
                                            setArchivoResultado(file);
                                        }
                                    }}
                                />
                            </Button>
                        )}
                    </Grid>
                </Grid>

                <TextField
                    label={t('drawer.resultado.observations')}
                    multiline
                    rows={3}
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    fullWidth
                    sx={{ mt: 3, width: '300px' }}
                    disabled={isReadOnly}
                />
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="outlined" onClick={handleCloseDrawer} sx={{ mr: 1 }}>
                    {t('drawer.cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={isReadOnly || !fechaResultado || isLoading}
                    sx={{ textTransform: 'none', minWidth: 100 }}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : t('drawer.save')}
                </Button>
            </Box>
        </Box>
    );
};

export default PeriodicosAsignarResultadoDrawer;
